/**
 * The days «Мій день» and the week overlay have already fetched, kept for the
 * life of the tab.
 *
 * Module-level rather than component state on purpose: the card unmounts every
 * time he leaves «Сьогодні», and re-fetching a week he already walked through
 * would make the `‹ ›` feel slower the more he used it.
 *
 * It lives here rather than inside `DayTimeline` so `useAuth` can empty it
 * without importing a card into the auth provider — the import would be a
 * cycle (the card reads `useAuth` for the user id) and would drag the grid,
 * the agenda maths and the icon set into the bundle that renders the login
 * form.
 *
 * Two things keep one account's calendar off another's screen, and both are
 * deliberate. The key carries the user id, because logging out is pure React
 * state — no reload — so the map outlives the session that filled it. And
 * {@link clearDayCache} empties it on every login and logout, so a shared
 * machine keeps nothing of his day in memory after he leaves.
 *
 * Entries carry the minute they arrived. Past {@link DAY_FRESH_MS} a day is
 * *stale*, not gone: it is still handed back so the grid paints instantly, and
 * the caller re-asks Google behind it and swaps the answer in. A calendar that
 * blanks itself to prove it is current is worse than one that is ten minutes
 * behind for a second.
 */
import type { CalendarDay } from "../types";

/** How long a fetched day counts as current. */
export const DAY_FRESH_MS = 10 * 60_000;

interface Entry {
  value: CalendarDay;
  fetchedAt: number;
}

export interface CachedDay {
  value: CalendarDay;
  /** Older than {@link DAY_FRESH_MS} — still worth drawing, worth re-asking. */
  stale: boolean;
}

const days = new Map<string, Entry>();

/** Told when the map is emptied, so a mounted grid can go and re-read its day. */
const listeners = new Set<() => void>();

const key = (userId: string, day: string): string => `${userId}|${day}`;

/**
 * The stored day for this account, fresh or stale — never another account's.
 *
 * `undefined` means "nothing to draw yet"; a `stale: true` answer means "draw
 * this now, and ask again".
 */
export function getCachedDay(
  userId: string,
  day: string,
  now: number = Date.now(),
): CachedDay | undefined {
  const entry = days.get(key(userId, day));
  if (!entry) return undefined;
  return { value: entry.value, stale: now - entry.fetchedAt >= DAY_FRESH_MS };
}

export function setCachedDay(
  userId: string,
  day: string,
  value: CalendarDay,
  now: number = Date.now(),
): void {
  days.set(key(userId, day), { value, fetchedAt: now });
}

/**
 * Drop every cached day and tell whoever is drawing one.
 *
 * `AuthProvider` calls this when the session changes, and «Оновити» on
 * «Сьогодні» calls it because "refresh this screen" has to include the day the
 * hour grid is standing on — clearing the map alone would only take effect the
 * next time he pressed `‹`, which is a button with no visible effect.
 */
export function clearDayCache(): void {
  days.clear();
  for (const listener of [...listeners]) listener();
}

/** Subscribe to {@link clearDayCache}. Returns the unsubscribe. */
export function subscribeDayCache(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
